import { NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { Events } from '@df/core/service/event';
import { DfDomModule } from '@df/dom/df-dom.module';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ZippyService } from '@df/legacy/ui/zippy/zippy.service';
import { OnChange } from '@df/ng/on-change';
import { BodyClassService } from '@df/ui/body-class.service';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { CountdownComponent } from '@df/ui/display/countdown.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent } from '@icms/core/content';
import { filter } from 'rxjs/operators';
import { PageHeaderComponent } from '../header/page-header.component';
import { IPageHeaderBlockContent } from '../header/page-header.interface';
import { SpecialMenuComponent } from '../special-menu/special-menu.component';
import { App } from '@df/core/app';

@Component({
    selector: 'promo-bar',
    templateUrl: './promo-bar.component.html',
    styleUrls: ['./promo-bar.component.scss'],
    standalone: true,
    imports: [
        CountdownComponent,
        DfUiModule,
        DfDomModule,
        HrefComponent,
        ICmsClientModule,
        NgClass,
        SizeClassDirective,
        SpecialMenuComponent,
        TranslationModule,
        ZippyModule,
        NgIf,
        NgSwitch,
        NgSwitchCase
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoBarComponent extends UiComponent implements OnInit, OnDestroy {
    @ICmsContent({
        watchDepth: 1
    })
    @Input({ required: true })
    data!: IPageHeaderBlockContent;
    /**
     * If hidden check
     */
    @Output()
    hiddenChange = new EventEmitter<boolean>();
    /**
     * If hide-up animation can be applied
     * it cannot be applied if bar is initially closed (not to appar cloasing on load)
     */
    canAnimate = false;
    @OnChange<boolean>(function (this: PromoBarComponent, value: boolean) {
        this.hiddenChange.emit(value);
    })
    hidden = false;
    /**
     * If is hidden
     */
    private readonly storageId = 'promoBarHidden';
    constructor(
        private events: Events,
        private sessionStorageService: SessionStorageService,
        private bodyClassService: BodyClassService,
        private pageHeaderComponent: PageHeaderComponent,
        private elementRef: ElementRef,
        private router: Router,
        private zippyService: ZippyService,
        private app: App
    ) {
        super();
    }
    override ngOnInit(): void {
        this.hidden = !this.canShow();
        if (!this.hidden) {
            this.canAnimate = true;
            this.bodyClassService.add('promo-bar-open', this);
        }
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            this.onRouteChange();
        });
        super.ngOnInit();
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, true);
    }

    onRouteChange(): void {
        this.zippyService.close('special-menu');
    }

    override ngOnDestroy(): void {
        this.bodyClassService.remove('promo-bar-open', this);
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);
    }
    close(): void {
        this.sessionStorageService.set(this.storageId, 1);
        this.bodyClassService.remove('promo-bar-open', this);
        this.hidden = true;
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);
        this.detectChanges();
        this.timeoutService.setTimeout(() => {
            this.events.broadcast('sticky.update');
        }, 250);
    }
    private canShow(): boolean {
        return (
            !this.app.isPlatformBrowser ||
            !this.sessionStorageService.get(this.storageId) ||
            this.client.isCms ||
            !this.data?.promobar_closeable
        );
    }

    getUkDate(dateStr: string, timeStr: string): Date | undefined {
        const date = new Date(`${dateStr}, ${timeStr}`);
        return date ? date : undefined;
    }
    get backgroundColors() {
        const colorRight = this.data.promobarEndColor;
        const colorLeft = this.data.promoBarStartColor;
        return `background: linear-gradient( 89.96deg, ${colorLeft} 0%, ${colorRight} 100%);`;
    }
}
